import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import axiosInstance from "../../api/axios";
import { createAxiosInstance } from "../axiosSettings";

// Helper function to normalize image path from backend: convert /uploads/ to /uploads/products/
const normalizeImagePath = (imagePath) => {
    if (!imagePath || typeof imagePath !== 'string') return imagePath;
    // If it's /uploads/filename.png, convert to /uploads/products/filename.png
    if (imagePath.startsWith('/uploads/') && !imagePath.startsWith('/uploads/service/')) {
        return imagePath.replace('/uploads/', '/uploads/service/');
    }
    return imagePath;
};

// Helper function to strip uploads prefix from image path (for sending to backend)
const stripUploadsPrefix = (imagePath) => {
    if (!imagePath || typeof imagePath !== 'string') return imagePath;
    // Strip /uploads/products/ prefix to get just filename
    if (imagePath.startsWith('/uploads/service/')) {
        return imagePath.replace('/uploads/service/', '');
    }
    // Strip /uploads/ prefix to get just filename
    if (imagePath.startsWith('/uploads/')) {
        return imagePath.replace('/uploads/', '');
    }
    return imagePath;
};

// Fetch all products for a specific service
export const fetchServices = createAsyncThunk(
    "products/fetchServices",
    async ({categoryId, subCategoryId}, thunkAPI) => {
        try {
            const axiosInstance = await createAxiosInstance();
            const response = await axiosInstance.get(`/admin/services/${categoryId}/sub-category/${subCategoryId}`);
            return response.data;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Add a product
export const addProduct = createAsyncThunk(
    "products/addProduct",
    async (payload, thunkAPI) => {
        try {
            const formData = new FormData();
            if (payload.categoryId) formData.append('categoryId', payload.categoryId);
            if (payload.subCategoryId) formData.append('subCategoryId', payload.subCategoryId);
            if (payload.title) formData.append('title', payload.title);
            if (payload.description) formData.append('description', payload.description);
            if (payload.price) formData.append('price', payload.price);
            if (payload.type) formData.append('type', payload.type);
            if (payload.status !== undefined) formData.append('status', payload.status || "Active");

            // Handle image upload
            if (payload.image instanceof File || payload.image instanceof Blob) {
                formData.append('image', payload.image); // new upload
            } else if (typeof payload.image === 'string') {
                // Strip /uploads/ or /uploads/products/ prefix if present, keep only filename
                const imagePath = stripUploadsPrefix(payload.image);
                formData.append('image', imagePath); // existing image
            }
            const axiosInstance = await createAxiosInstance();
            const response = await axiosInstance.post("/admin/services", formData);
            return response.data.product || response.data;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Update a product
export const updateProduct = createAsyncThunk(
    "products/updateProduct",
    async ({ id, data }, thunkAPI) => {
        try {
            const formData = new FormData();
            if (data.categoryId) formData.append('categoryId', data.categoryId);
            if (data.subCategoryId) formData.append('subCategoryId', data.subCategoryId);
            if (data.title) formData.append('title', data.title);
            if (data.description) formData.append('description', data.description);
            if (data.price) formData.append('price', data.price);
            if (data.type) formData.append('type', data.type);
            if (data.status !== undefined) formData.append('status', data.status || "Active");

            // Handle image upload
            if (data.image instanceof File || data.image instanceof Blob) {
                formData.append('image', data.image); // new upload
            } else if (typeof data.image === 'string') {
                // Strip /uploads/ or /uploads/products/ prefix if present, keep only filename
                const imagePath = stripUploadsPrefix(data.image);
                formData.append('image', imagePath); // existing image
            }
            const axiosInstance = await createAxiosInstance();
            const response = await axiosInstance.post(`/admin/services/update/${id}`, formData);
            return response.data || response.data;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Delete a product
export const deleteService = createAsyncThunk(
    "products/deleteService",
    async (id, thunkAPI) => {
        try {
            const axiosInstance = await createAxiosInstance();
            await axiosInstance.delete(`/admin/services/${id}`);
            return id;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.response?.data || err.message);
        }
    }
);

const productsSlice = createSlice({
    name: "services",
    initialState: {
        services: [],
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchServices.pending, (state) => { state.loading = true; })
            .addCase(fetchServices.fulfilled, (state, action) => {
                state.loading = false;
                // Normalize image paths: convert /uploads/ to /uploads/products/
                const normalizedProducts = Array.isArray(action.payload)
                    ? action.payload?.data?.map(product => {
                        if (product.image && typeof product.image === 'string') {
                            return { ...product, image: normalizeImagePath(product.image) };
                        }
                        return product;
                    })
                    : action.payload;
                state.services = normalizedProducts;
            })
            .addCase(fetchServices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add
            .addCase(addProduct.pending, (state) => { state.loading = true; })
            .addCase(addProduct.fulfilled, (state, action) => {
                state.loading = false;
                // Normalize image path: convert /uploads/ to /uploads/products/
                const newService = { ...action.payload?.service };
                if (newService.image && typeof newService.image === 'string') {
                    newService.image = normalizeImagePath(newService.image);
                }
                state.services.data.push(newService);
            })
            .addCase(addProduct.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
            // Update
            .addCase(updateProduct.pending, (state) => { state.loading = true; })
            .addCase(updateProduct.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.services?.data.findIndex(p => p._id === action.payload?.service?._id);
                if (index !== -1) {
                    // Normalize image path: convert /uploads/ to /uploads/products/
                    const updatedProduct = { ...action.payload?.service };
                    if (updatedProduct.image && typeof updatedProduct.image === 'string') {
                        updatedProduct.image = normalizeImagePath(updatedProduct.image);
                    }
                    state.services.data[index] = updatedProduct;
                }
            })
            .addCase(updateProduct.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
            // Delete
            .addCase(deleteService.pending, (state) => { state.loading = true; })
            .addCase(deleteService.fulfilled, (state, action) => {
                state.loading = false;
                state.services = state.services?.data.filter(p => p._id !== action.payload);
            })
            .addCase(deleteService.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    },
});

export default productsSlice.reducer;
